import { isFactoryProvider, isTypeProvider, isValueProvider } from "./types.js";
import { getInjectedProperties, getInjectedTokens, getOptionalParameters, getOptionalProperties, getParameterTypes, getSingletonMetadata, hasPropertyDependencies, isInjectable, isSingleton } from "./decorators.js";
class Container {
    constructor(options = {}){
        this.providers = new Map();
        this.singletons = new Map();
        this.singletonCreationPromises = new Map();
        this.metadataCache = new Map();
        this.autoRegisterMutex = new Map();
        this.performanceMetrics = {
            resolutionCount: 0,
            avgResolutionTime: 0,
            cacheHitRate: 0,
            autoRegistrationCount: 0
        };
        this.options = Object.freeze({
            strict: true,
            autoRegister: false,
            enablePerformanceMonitoring: false,
            metadataCacheSize: 1000,
            ...options
        });
    }
    register(provider) {
        this.providers.set(provider.token, provider);
    }
    registerSingleton(token, useClass) {
        this.register({
            type: 'type',
            token,
            useClass,
            singleton: true
        });
    }
    registerTransient(token, useClass) {
        this.register({
            type: 'type',
            token,
            useClass,
            singleton: false
        });
    }
    registerValue(token, value) {
        this.register({
            type: 'value',
            token,
            useValue: value
        });
    }
    registerFactory(token, factory, deps = [], singleton = false) {
        this.register({
            type: 'factory',
            token,
            useFactory: factory,
            deps,
            singleton
        });
    }
    getCachedMetadata(constructor) {
        if (this.metadataCache.has(constructor)) {
            if (this.options.enablePerformanceMonitoring) this.performanceMetrics.cacheHitRate++;
            return this.metadataCache.get(constructor);
        }
        const metadata = {
            paramTypes: getParameterTypes(constructor),
            injectedTokens: getInjectedTokens(constructor),
            optionalParams: getOptionalParameters(constructor),
            injectedProperties: getInjectedProperties(constructor),
            optionalProperties: getOptionalProperties(constructor),
            hasPropertyDeps: hasPropertyDependencies(constructor)
        };
        if (this.metadataCache.size >= this.options.metadataCacheSize) {
            const firstKey = this.metadataCache.keys().next().value;
            if (firstKey) this.metadataCache.delete(firstKey);
        }
        this.metadataCache.set(constructor, metadata);
        return metadata;
    }
    async optimizedAutoRegister(token) {
        if (this.providers.has(token)) return;
        if (this.autoRegisterMutex.has(token)) return void await this.autoRegisterMutex.get(token);
        const registerPromise = (async ()=>{
            if (!this.providers.has(token) && 'function' == typeof token && (isInjectable(token) || isSingleton(token))) {
                const constructorWithMeta = token;
                const singletonMeta = getSingletonMetadata(token);
                if (singletonMeta?.autoRegister || constructorWithMeta.__autoRegister__?.singleton) this.registerSingleton(token, token);
                else this.registerTransient(token, token);
                if (this.options.enablePerformanceMonitoring) this.performanceMetrics.autoRegistrationCount++;
            }
        })();
        this.autoRegisterMutex.set(token, registerPromise);
        await registerPromise;
        this.autoRegisterMutex.delete(token);
    }
    get(token, options = {}) {
        this.options.enablePerformanceMonitoring && performance.now();
        if (this.singletons.has(token)) return this.singletons.get(token);
        const provider = this.providers.get(token);
        if (!provider) {
            if (this.options.autoRegister && 'function' == typeof token) {
                if (isInjectable(token) || isSingleton(token)) {
                    const autoRegisterKey = `__autoRegisterGet_${token.name}_${String(token)}`;
                    if (!this.__autoRegisterGetInProgress) this.__autoRegisterGetInProgress = new Set();
                    if (!this.__autoRegisterGetInProgress.has(autoRegisterKey)) {
                        this.__autoRegisterGetInProgress.add(autoRegisterKey);
                        try {
                            if (!this.providers.has(token)) {
                                const constructorWithMeta = token;
                                const singletonMeta = getSingletonMetadata(token);
                                if (singletonMeta?.autoRegister || constructorWithMeta.__autoRegister__ && constructorWithMeta.__autoRegister__.singleton) this.registerSingleton(token, token);
                                else this.registerTransient(token, token);
                            }
                        } finally{
                            this.__autoRegisterGetInProgress.delete(autoRegisterKey);
                        }
                    }
                    return this.get(token, options);
                }
            }
            if (!options.optional) throw new Error(`No provider found for token: ${String(token)}`);
            return;
        }
        let instance;
        if (isValueProvider(provider)) instance = provider.useValue;
        else if (isFactoryProvider(provider)) {
            const deps = provider.deps ? this.resolveDependenciesSync(provider.deps) : [];
            const factoryResult = provider.useFactory(...deps);
            if (factoryResult instanceof Promise) throw new Error('Cannot get async factory result synchronously. Use resolve() instead.');
            instance = factoryResult;
        } else if (isTypeProvider(provider)) instance = this.createInstanceSync(provider.useClass);
        else throw new Error(`Unknown provider type: ${provider.type}`);
        if ('value' !== provider.type && 'singleton' in provider && provider.singleton) this.singletons.set(token, instance);
        return instance;
    }
    async resolve(token, options = {}) {
        if (this.singletons.has(token)) return this.singletons.get(token);
        if (this.singletonCreationPromises.has(token)) return await this.singletonCreationPromises.get(token);
        const provider = this.providers.get(token);
        if (!provider) {
            if (this.options.autoRegister && 'function' == typeof token) {
                if (isInjectable(token) || isSingleton(token)) {
                    const autoRegisterKey = `__autoRegisterResolve_${token.name}_${String(token)}`;
                    if (!this.__autoRegisterResolveInProgress) this.__autoRegisterResolveInProgress = new Set();
                    if (this.__autoRegisterResolveInProgress.has(autoRegisterKey)) await new Promise((resolve)=>setTimeout(resolve, 1));
                    else {
                        this.__autoRegisterResolveInProgress.add(autoRegisterKey);
                        try {
                            if (!this.providers.has(token)) {
                                const constructorWithMeta = token;
                                const singletonMeta = getSingletonMetadata(token);
                                if (singletonMeta?.autoRegister || constructorWithMeta.__autoRegister__ && constructorWithMeta.__autoRegister__.singleton) this.registerSingleton(token, token);
                                else this.registerTransient(token, token);
                            }
                        } finally{
                            this.__autoRegisterResolveInProgress.delete(autoRegisterKey);
                        }
                    }
                    return this.resolve(token, options);
                }
            }
            if (!options.optional) throw new Error(`No provider found for token: ${String(token)}`);
            return;
        }
        let instance;
        if ('value' !== provider.type && 'singleton' in provider && provider.singleton) {
            const creationPromise = this.createSingletonInstance(token, provider);
            this.singletonCreationPromises.set(token, creationPromise);
            try {
                instance = await creationPromise;
            } finally{
                this.singletonCreationPromises.delete(token);
            }
        } else if (isValueProvider(provider)) instance = provider.useValue;
        else if (isFactoryProvider(provider)) {
            const deps = provider.deps ? await this.resolveDependencies(provider.deps) : [];
            instance = await provider.useFactory(...deps);
        } else if (isTypeProvider(provider)) instance = await this.createInstance(provider.useClass);
        else throw new Error(`Unknown provider type: ${provider.type}`);
        return instance;
    }
    async createSingletonInstance(token, provider) {
        if (this.singletons.has(token)) return this.singletons.get(token);
        let instance;
        if (isFactoryProvider(provider)) {
            const deps = provider.deps ? await this.resolveDependencies(provider.deps) : [];
            instance = await provider.useFactory(...deps);
        } else if (isTypeProvider(provider)) instance = await this.createInstance(provider.useClass);
        else throw new Error(`Unknown provider type: ${provider.type}`);
        this.singletons.set(token, instance);
        return instance;
    }
    async createInstance(constructor) {
        const paramTypes = getParameterTypes(constructor);
        const injectedTokens = getInjectedTokens(constructor);
        const optionalParams = getOptionalParameters(constructor);
        const dependencies = [];
        const maxLength = Math.max(paramTypes.length, Object.keys(injectedTokens).length, Object.keys(optionalParams).length, constructor.length || 0);
        for(let i = 0; i < maxLength; i++){
            const token = injectedTokens[i] || paramTypes[i];
            const isOptional = optionalParams[i] || false;
            if (token) try {
                const dependency = await this.resolve(token, {
                    optional: isOptional
                });
                dependencies.push(dependency);
            } catch (error) {
                if (isOptional) dependencies.push(void 0);
                else throw error;
            }
            else dependencies.push(void 0);
        }
        return new constructor(...dependencies);
    }
    async resolveDependencies(tokens) {
        return Promise.all(tokens.map((token)=>this.resolve(token)));
    }
    resolveDependenciesSync(tokens) {
        return tokens.map((token)=>this.get(token));
    }
    createInstanceSync(constructor) {
        const paramTypes = getParameterTypes(constructor);
        const injectedTokens = getInjectedTokens(constructor);
        const optionalParams = getOptionalParameters(constructor);
        const dependencies = [];
        const maxLength = Math.max(paramTypes.length, Object.keys(injectedTokens).length, Object.keys(optionalParams).length, constructor.length || 0);
        for(let i = 0; i < maxLength; i++){
            const token = injectedTokens[i] || paramTypes[i];
            const isOptional = optionalParams[i] || false;
            if (token) try {
                const dependency = this.get(token, {
                    optional: isOptional
                });
                dependencies.push(dependency);
            } catch (error) {
                if (isOptional) dependencies.push(void 0);
                else throw error;
            }
            else dependencies.push(void 0);
        }
        return new constructor(...dependencies);
    }
    async injectProperties(instance) {
        if (!instance || 'object' != typeof instance) return;
        const constructor = instance.constructor;
        if (!hasPropertyDependencies(constructor)) return;
        const injectedProperties = getInjectedProperties(constructor);
        const optionalProperties = getOptionalProperties(constructor);
        for (const [propertyKey, token] of Object.entries(injectedProperties)){
            const isOptional = optionalProperties[propertyKey] || false;
            try {
                const dependency = await this.resolve(token, {
                    optional: isOptional
                });
                if (void 0 !== dependency) instance[propertyKey] = dependency;
            } catch (error) {
                if (!isOptional) throw new Error(`Failed to inject property '${String(propertyKey)}' in ${constructor.name}: ${error instanceof Error ? error.message : String(error)}`);
            }
        }
    }
    injectPropertiesSync(instance) {
        if (!instance || 'object' != typeof instance) return;
        const constructor = instance.constructor;
        if (!hasPropertyDependencies(constructor)) return;
        const injectedProperties = getInjectedProperties(constructor);
        const optionalProperties = getOptionalProperties(constructor);
        for (const [propertyKey, token] of Object.entries(injectedProperties)){
            const isOptional = optionalProperties[propertyKey] || false;
            try {
                const dependency = this.get(token, {
                    optional: isOptional
                });
                if (void 0 !== dependency) Reflect.set(instance, propertyKey, dependency);
            } catch (error) {
                if (!isOptional) throw new Error(`Failed to inject property '${String(propertyKey)}' in ${constructor.name}: ${error instanceof Error ? error.message : String(error)}`);
            }
        }
    }
    async createWithPropertyInjection(constructor) {
        const instance = await this.createInstance(constructor);
        await this.injectProperties(instance);
        return instance;
    }
    createWithPropertyInjectionSync(constructor) {
        const instance = this.createInstanceSync(constructor);
        this.injectPropertiesSync(instance);
        return instance;
    }
    has(token) {
        return this.providers.has(token);
    }
    clear() {
        this.providers.clear();
        this.singletons.clear();
    }
    createChild(options) {
        const child = new Container({
            ...this.options,
            ...options
        });
        this.providers.forEach((provider, token)=>{
            child.providers.set(token, provider);
        });
        return child;
    }
    getTokens() {
        return Array.from(this.providers.keys());
    }
    getPerformanceMetrics() {
        return this.options.enablePerformanceMonitoring ? {
            ...this.performanceMetrics
        } : null;
    }
    resetPerformanceMetrics() {
        this.performanceMetrics = {
            resolutionCount: 0,
            avgResolutionTime: 0,
            cacheHitRate: 0,
            autoRegistrationCount: 0
        };
    }
    registerBatch(providers) {
        providers.forEach((provider)=>this.providers.set(provider.token, provider));
    }
    preloadMetadata(constructors) {
        constructors.forEach((constructor)=>{
            if (!this.metadataCache.has(constructor)) this.getCachedMetadata(constructor);
        });
    }
    getStats() {
        return {
            providersCount: this.providers.size,
            singletonsCount: this.singletons.size,
            metadataCacheSize: this.metadataCache.size,
            autoRegisterMutexSize: this.autoRegisterMutex.size,
            performanceMetrics: this.getPerformanceMetrics()
        };
    }
}
export { Container };
